import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import Cookies from 'js-cookie';
import React, { useEffect, useRef, useState } from 'react';

import ContentContainer from '../components/Common/ContentContainer';
import JobPostings from '../components/Common/JobPostings';
import { ArrowLink } from '../components/Common/Links';
import OverlappingHero from '../components/Common/OverlappingHero';
import PageSection from '../components/Common/PageSection';
import RichTextConverter from '../components/Common/RichText/RichTextConverter';
import ScrollTo, { smoothScrollToRef } from '../components/Common/ScrollTo';
import Accordion from '../components/Generic/Accordion';
import GroupedContent from '../components/Generic/GroupedContent';
import IconList from '../components/Generic/IconList';
import Layout from '../components/Layouts/Layout';
import MarketingSeo from '../components/MarketingSeo';
import * as styles from './careers.module.css';

const Careers = ({ data: { page }, location }) => {
  const scrollRef = useRef();
  const onCta = (e) => {
    e.preventDefault();
    smoothScrollToRef(scrollRef, null, 84);
  };
  const [tracker, setTracker] = useState(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const src = params.get('gh_src');
    if (src) {
      setTracker(src);
      Cookies.set('gh_src', src);
    }
  }, [tracker]);

  return (
    <Layout>
      <MarketingSeo meta={page.pageMetaData} slug={location.pathname} />
      <PageSection isTopSection={true}>
        <OverlappingHero
          copy={page.headingIntroCopy}
          image={page.headingImage}
          heading={page.heading}
          cta={page.headingCtaLabel}
          onCta={onCta}
          headingCtas={page.headingCtAs}
        />
      </PageSection>
      {page.pageSections.map((section, i) => {
        let jobBoardUsed = false;
        switch (section.__typename) {
          case 'ContentfulGroupedContent': {
            if (section.type === 'Three Up') {
              return <GroupedContent section={section} i={i} />;
            } else if (section.type === 'Icon List') {
              return (
                <PageSection key={i}>
                  <IconList
                    list={section.content}
                    heading={section.title}
                    gatsbyImage={section.mainImage.gatsbyImageData}
                  />
                </PageSection>
              );
            } else if (section.type === 'Template Standard' || section.type === 'Accordion') {
              const hiringProcessList = section.content?.map((item, i) => {
                return {
                  title: item.title,
                  supportingText: item.body,
                };
              });
              return (
                <PageSection key={i} slim>
                  <hr />
                  <div className={styles.hiringContent}>
                    <h3>{section.title}</h3>
                    <div className={styles.hiringCopy}>
                      <RichTextConverter copy={section.body} />
                    </div>
                  </div>
                  <Accordion items={hiringProcessList} numbered />
                </PageSection>
              );
            }
            break;
          }
          case 'ContentfulContentCard': {
            if (jobBoardUsed) {
              break;
            }
            jobBoardUsed = true;
            return (
              <div key={i}>
                <ScrollTo reference={scrollRef} />
                <PageSection slim>
                  <hr />
                  <JobPostings tracker={tracker} heading={section.title} location={location} />
                </PageSection>
                <ContentContainer>
                  <hr />
                  <div className={styles.general}>
                    <h3>{section.subtitle}</h3>
                    <div>
                      <RichTextConverter copy={section.copy} />
                    </div>
                  </div>
                </ContentContainer>
              </div>
            );
          }
          case 'ContentfulMediaGroup': {
            if (section.type === 'Mosaic') {
              return (
                <PageSection key={i}>
                  <div className={styles.imageMosaic}>
                    {section.media.map((img, j) => {
                      const classNames = [
                        styles.firstImg,
                        styles.secondImg,
                        styles.thirdImg,
                        styles.fourthImg,
                        styles.fifthImg,
                      ];
                      let className = classNames[j];
                      return (
                        <GatsbyImage
                          key={j}
                          image={img.gatsbyImageData}
                          alt={img.title}
                          className={className}
                          loading="lazy"
                        />
                      );
                    })}
                  </div>
                </PageSection>
              );
            } else {
              return (
                <PageSection key={i} className={styles.grey} wide>
                  <div className={styles.wide}>
                    <div className={styles.badgeSection}>
                      <h3>{section.title}</h3>
                      <div className={styles.badges}>
                        <img
                          src={'https://www.glassdoor.com/api/widget/verticalStarRating.htm?e=1168508'}
                          alt={'Glassdoor Rating'}
                        />
                        {section.media.map((image, i) => {
                          if (i >= 3) return null;
                          return <img key={i} src={image.file.url} alt={image.title} />;
                        })}
                      </div>
                    </div>
                  </div>
                </PageSection>
              );
            }
          }
          case 'ContentfulCallToAction': {
            return (
              <PageSection key={i} className={styles.cta}>
                <div className={styles.general}>
                  <h3>{section.title}</h3>
                  <div>
                    <p>{section.description?.description}</p>
                    <div>
                      <ArrowLink label={section.ctaLabel} url={section.url} />
                    </div>
                  </div>
                </div>
              </PageSection>
            );
          }
          default:
            return null;
        }
        return null;
      })}
    </Layout>
  );
};

export default Careers;

export const query = graphql`
  query CareersTemplateQuery($slug: String!) {
    page: contentfulMultipurposePageTemplate(slug: { eq: $slug }) {
      ...MultipurposePage
    }
  }
`;
